import React, { useContext } from 'react';
import { selector } from 'recoil';
import { BananaContext } from '@wikimedia/react.i18n';

import DashboardIcon from '@assets/icons/DashboardIcon';
import GettingStartedIcon from '@assets/icons/GettingStartedIcon';
import SettingsIcon from '@assets/icons/SettingsIcon';
import DevelopersIcon from '@assets/icons/DevelopersIcon';
import AnalyticsIcon from '@assets/icons/AnalyticsIcon';
import ManagementIcon from '@assets/icons/ManagementIcon';
import PaymentsIcon from '@assets/icons/PaymentsIcon';
import LogoutIcon from '@assets/icons/LogoutIcon';
import AIAssistantIcon from '@assets/icons/AIAssistantIcon';

import { useAuth } from '@context/Auth0Context';
import { PERMISSIONS } from '@services/config';
import { Box, Chip } from '@mui/material';
import PayByLinkIcon from '@assets/icons/PayByLinkIcon';

export const navigationRoutesSelector = selector({
  key: 'navigationRoutesSelector',
  get: () => {
    const translator = useContext(BananaContext);
    const { logout } = useAuth();

    const gettingStartedMenu = {
      label: translator.i18n('getting-started-title'),
      icon: <GettingStartedIcon />,
      slug: 'getting-started',
      id: 'getting-started-menu',
      children: [],
    };

    const dashboardMenu = {
      label: translator.i18n('dashboard-title'),
      icon: <DashboardIcon />,
      slug: 'dashboard',
      id: 'dashboard-menu',
      children: [],
    };

    const analyticsMenu = {
      label: translator.i18n('analytics-title'),
      icon: <AnalyticsIcon />,
      slug: 'analytics',
      id: 'analytics-menu',
      children: [
        {
          label: translator.i18n('statistics-title'),
          slug: 'statistics',
          id: 'statistics-menu',
          permissions: PERMISSIONS.REPORTS_STATISTICS,
          children: [],
        },
        {
          label: translator.i18n('revenue'),
          slug: 'revenue',
          id: 'revenue-menu',
          permissions: PERMISSIONS.ANALYTICS_CURRENT_BALANCE,
          children: [],
        },
      ],
    };

    const paymentsMenu = {
      label: translator.i18n('payments-title'),
      icon: <PaymentsIcon />,
      slug: 'payments',
      id: 'payments-menu',
      children: [
        {
          label: <>{translator.i18n('transactions-title')}</>,
          slug: 'transactions-new',
          id: 'transactions-menu',
          permissions: PERMISSIONS.REPORTS_TRANSACTIONS,
          children: [],
        },
        {
          label: translator.i18n('orders-title'),
          slug: 'orders-new',
          id: 'orders-menu',
          permissions: PERMISSIONS.REPORTS_TRANSACTIONS,
          children: [],
        },
        {
          label: translator.i18n('shifts-title'),
          slug: 'shifts',
          id: 'shifts-menu',
          permissions: PERMISSIONS.SHIFTS,
          children: [],
        },
        {
          label: translator.i18n('chargeback-title'),
          slug: 'chargebacks',
          id: 'chargebacks-menu',
          children: [],
        },
      ],
    };

    const payByLinkMenu = {
      label: translator.i18n('pay-by-link-title'),
      icon: <PayByLinkIcon />,
      slug: 'paybylink',
      id: 'paybylink-menu',
      permissions: PERMISSIONS.PAYBYLINK,
      children: [],
    };

    const administrationMenu = {
      label: translator.i18n('administration-title'),
      icon: <ManagementIcon />,
      slug: 'administration',
      id: 'administration-menu',
      children: [
        {
          label: translator.i18n('settlements'),
          slug: 'settlement-files',
          id: 'settlement-files-menu',
          permissions: PERMISSIONS.PAYMENTS_SETTLEMENTS,
          children: [],
        },
        {
          label: translator.i18n('payouts-title'),
          slug: 'payouts',
          id: 'payouts-menu',
          permissions: PERMISSIONS.PAYMENTS_PAYOUTS,
          children: [],
        },
      ],
    };

    const settingsMenu = {
      label: translator.i18n('settings-title'),
      icon: <SettingsIcon />,
      slug: 'settings',
      id: 'settings-menu',
      children: [
        {
          label: translator.i18n('team-users'),
          slug: 'users',
          id: 'users-menu',
          permissions: PERMISSIONS.USERS,
          children: [],
        },
        {
          label: translator.i18n('user'),
          slug: 'user',
          id: 'user-menu',
          permissions: PERMISSIONS.SETTINGS,
          children: [],
        },
        {
          label: translator.i18n('units'),
          slug: 'stores',
          id: 'stores-menu',
          permissions: PERMISSIONS.SETTINGS,
          children: [],
        },
      ],
    };

    const developersMenu = {
      label: translator.i18n('developers-title'),
      icon: <DevelopersIcon />,
      slug: 'developers',
      id: 'developers-menu',
      permissions: PERMISSIONS.DEVELOPERS,
      children: [
        {
          label: translator.i18n('api-keys'),
          slug: 'apikeys',
          id: 'apikeys-menu',
          children: [],
        },
        {
          label: translator.i18n('webhooks'),
          slug: 'webhooks',
          id: 'webhooks-menu',
          children: [],
        },
      ],
    };

    // AI Smart Assistant Menu Option
    const aiSmartAssistantMenu = {
      label: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {translator.i18n('ai_smart_assistant_title')}
          </Box>
          <Chip
            size="small"
            label="BETA"
            color="warning"
            sx={{
              height: 16,
              flexShrink: 0,
              '& .MuiChip-label': {
                px: 0.5,
                fontSize: '0.5rem',
                fontWeight: 600,
                lineHeight: 1,
              },
            }}
          />
        </Box>
      ),
      icon: <AIAssistantIcon />,
      slug: 'ai-smart-assistant',
      id: 'ai-smart-assistant-menu',
      onClick: () => {
        const event = new CustomEvent('openAISmartAssistant');
        window.dispatchEvent(event);
        return false;
      },
    };

    const logoutMenu = {
      label: translator.i18n('logout'),
      icon: <LogoutIcon />,
      id: 'logout-menu',
      onClick: () => {
        logout({
          returnTo: window.location.origin,
        });
      },
    };

    return {
      topItems: [
        gettingStartedMenu,
        dashboardMenu,
        analyticsMenu,
        paymentsMenu,
        payByLinkMenu,
        administrationMenu,
        settingsMenu,
      ],
      bottomItems: [aiSmartAssistantMenu, developersMenu, logoutMenu],
    };
  },
});
